import {
  Component,
  forwardRef,
  Inject,
  Input,
  ChangeDetectorRef,
  NgZone,
  SimpleChanges,
  OnChanges,
  OnInit
}                             from '@angular/core';
import { BlockUI, NgBlockUI } from 'ng-block-ui';
import { TranslateService }   from '@ngx-translate/core';

import { HaynesProGroupModel }                  from '../typings/haynes-pro.group';
import { HaynesProService }                     from '../haynes-pro.service';
import { HaynesProSmartPackBulletinGroupModel } from '../typings/haynes-pro.smart-pack.bulletin-group';
import { HaynesProSmartPackCategoryModel }      from '../typings/haynes-pro.smart-pack.category';
import { HaynesProSmartPackSystemModel }        from '../typings/haynes-pro.smart-pack.system';
import { HaynesProSubGroupModel }               from '../typings/haynes-pro.sub-group';
import { MessagesService }                      from '../../messages/messages.service';
import { VehicleModel }                         from '../../vehicles/models/vehicle';

@Component({
  selector   : 'wor-haynes-pro-diagnostic-tool-smart-pack',
  styles: [ require('./haynes-pro.diagnostic-tool.smart-pack.component.scss') ],
  template: require('./haynes-pro.diagnostic-tool.smart-pack.component.html')
})
export class HaynesProDiagnosticToolSmartPackComponent implements OnChanges, OnInit {

  @BlockUI('haynesProSmartPackBlock') block: NgBlockUI;

  @Input() identifier : string;
  @Input() isActive   : boolean;
  @Input() subGroups  : Array<HaynesProSubGroupModel>;
  @Input() vehicle    : VehicleModel;

  bulletins : Array<HaynesProSmartPackBulletinGroupModel> = [];
  categories: Array<HaynesProSmartPackCategoryModel>      = [];
  subGroup  : HaynesProGroupModel;
  system    : HaynesProSmartPackSystemModel;

  isAnimating  = false;
  showDetails  = false;
  searchText   = '';

  constructor (
    private cdr              : ChangeDetectorRef,
    @Inject(forwardRef(() => HaynesProService))
    private haynesProService : HaynesProService,
    @Inject(forwardRef(() => MessagesService)) private messagesService  : MessagesService,
    private translateService : TranslateService,
    private zone             : NgZone
  ) {}

  ngOnChanges ( changes : SimpleChanges ) : void {
    if ( changes.isActive && !changes.isActive.isFirstChange() && changes.isActive.currentValue ) {
      const subGroup = this.subGroup
        ? this.subGroup
        : this.subGroups[0];

      subGroup.selected = true;
    }
  }

  ngOnInit () : void {
    if (this.isActive) {
      this.subGroups[0].selected = true;
    }
  }

  backToLists () : void {
    this.isAnimating = true;
    this.showDetails = false;

    this.cdr.markForCheck();

    setTimeout(() => this.isAnimating = false, 450);
  }

  getCategoryTitle ( category : string ) : string {
    switch (category) {
      case 'CASES':
        return this.translateService.instant('COMPANY_SPACE.INTEGRATIONS.HAYNES_PRO.CASES');
      case 'RECALLS':
        return this.translateService.instant('COMPANY_SPACE.INTEGRATIONS.HAYNES_PRO.RECALLS');
      case 'TSB':
        return this.translateService.instant('COMPANY_SPACE.INTEGRATIONS.HAYNES_PRO.TSB');
      default:
        return category;
    }
  }

  handleSearch ( query : string ) : void {
    this.subGroup = this.subGroup && this.subGroup.name
      ? this.subGroup
      : { name: 'QUICKGUIDES' };

    this.handleSubGroupSelection(this.subGroup, query);
  }

  handleSubGroupSelection ( subGroup : HaynesProGroupModel, query = '' ) : void {
    this.block.start();

    this.bulletins   = [];
    this.categories  = [];
    this.searchText  = query;
    this.showDetails = false;
    this.subGroup    = subGroup;

    Promise.resolve()
    .then(() => query
      ? this.haynesProService.searchSmartPack(this.identifier, this.vehicle, subGroup.name, query)
      : this.haynesProService.getSmartPack(this.identifier, this.vehicle, subGroup.name)
    )
    .then(( categories : Array<HaynesProSmartPackCategoryModel> ) => {
      this.categories = categories;

      if (!this.isActive) {
        this.haynesProService.updateSubscriptionStatus(true);
      }
    })
    .catch(err => this.messagesService.error(err))
    .finally(() => this.block.stop());
  }

  handleSystemSelection ( system: HaynesProSmartPackSystemModel, category : HaynesProSmartPackCategoryModel ) : void {
    this.block.start();

    this.bulletins       = [];
    this.isAnimating     = false;
    this.showDetails     = false;
    this.system          = system;

    this.cdr.markForCheck();

    this.haynesProService.getSmartPackBulletins(this.identifier, this.vehicle, category, system)
    .then( ( bulletins: Array<HaynesProSmartPackBulletinGroupModel> = [] ) => {
      this.bulletins = bulletins[0]?.systems || bulletins;

      this.zone.runOutsideAngular(() => {
        requestAnimationFrame(() => {
          requestAnimationFrame(() => {
            this.zone.run(() => {
              this.isAnimating = true;
              this.showDetails = true;

              this.cdr.markForCheck();

              setTimeout(() => this.isAnimating = false, 450);
            });
          });
        });
      });
    })
    .catch(err => this.messagesService.error(err))
    .finally(() => this.block.stop());
  }

  onCssTransitionEnd ( evt : TransitionEvent ) : void {
    const target = (evt.target as HTMLElement);

    if ( !target.classList.contains('shell-details') ) {
      return;
    }

    if ( evt.propertyName !== 'transform' && evt.propertyName !== 'opacity' ) {
      return;
    }

    this.isAnimating = false;
  }

  onReset () : void {
    this.searchText = '';

    this.resetSearch();
  }

  onSearch ( term : string ) : void {
    const query = ( term || '' ).trim();

    query
      ? this.handleSearch(query)
      : '';
  }

  resetSearch () : void {
    this.handleSubGroupSelection(this.subGroup);
  }
}